import { createTheme, Theme } from "@mui/material";
import { deepmerge } from '@mui/utils';
import { cssFluidSize } from "./styleHelpers";
import typeVars, { fontFamilyHeadings, fontFamilySansSerif } from "./typeVars";

declare module '@mui/material/styles' {
  interface TypographyVariants {
    small: React.CSSProperties;
    large: React.CSSProperties;
    tiny: React.CSSProperties;
    detail: React.CSSProperties;
    bold: React.CSSProperties;
    // cta: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    small?: React.CSSProperties;
    large?: React.CSSProperties;
    tiny?: React.CSSProperties;
    detail?: React.CSSProperties;
    bold: React.CSSProperties;

  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    small: true;
    large: true;
    tiny: true;
    detail: true;
    bold: true;
    // disable h3 variant
    // h3: false;
    // body2: false,
    // subtitle1: false,
    // subtitle2: false,
  }
}


export const typeStyle = {
  headingFont: {
    fontFamily: fontFamilyHeadings,
    fontStyle: 'normal',
    lineHeight: typeVars.headingsLineHeight,
    fontWeight: 800,
  },
  heading: {
    marginBottom: typeVars.headingsMarginBottom,
    marginTop: typeVars.headingsMarginTop
  },
  allCaps: {
  fontFamily: fontFamilySansSerif,
  fontStyle: 'normal',
  fontWeight: 500,
  },
  smallCaps: {
    textTransform: 'uppercase',
    fontWeight: typeVars.fontWeightNormal,
    fontSize: 14,
    lineHeight: 18/14,
    letterSpacing: '0.2em'
  },
  singleLine: { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: '100%' }
} as const



const theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: ``,
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          body1: 'p',
          small: 'p',
          large: 'p',
          tiny: 'p',
          detail: 'p',
          bold: 'p',
        },
      },
    },
  },
  typography: {
    fontFamily: fontFamilySansSerif,
    fontSize: typeVars.fontSizeBase,
    h1: {
      ...typeStyle.headingFont,
      ...typeStyle.heading,
      fontSize: cssFluidSize([40, 64]),
    },
    h2: {
      ...typeStyle.headingFont,
      ...typeStyle.heading,
      fontSize: cssFluidSize([38, 50]),
    },
    h3: {
      ...typeStyle.headingFont,
      ...typeStyle.heading,
      fontSize: cssFluidSize([30, 40]),
    },
    h4: {
      ...typeStyle.headingFont,
      ...typeStyle.heading,
      fontSize: cssFluidSize([28, 24]),
    },
    h5: {
      ...typeStyle.headingFont,
      ...typeStyle.heading,
      fontSize: cssFluidSize([19, 20]),
    },
    h6: {
      ...typeStyle.headingFont,
      ...typeStyle.heading,
      fontSize: cssFluidSize([22, 20]),
    },
    body1: {
      fontSize: 16, //cssFluidSize([16, 18]),
      fontWeight: 500,
      lineHeight: 1.5,
    },
    bold: {
      fontWeight: 700,
    },
    small: {
      fontSize: cssFluidSize([14, 16]),
      fontWeight: 500,
      lineHeight: 1.5,
    },
    large: {
      fontSize: cssFluidSize([16, 18]),
      fontWeight: 500,
      lineHeight: 1.5,
    },
    tiny: {
      fontSize: cssFluidSize([12, 12]),
      fontWeight: 500,
      lineHeight: 1.4,
    },
    detail: {
      fontSize: cssFluidSize([12, 12]),
      fontWeight: 500,
      lineHeight: 1.55,
      fontFamily: fontFamilyHeadings,
      textTransform: 'uppercase',
    },
    button: {
      fontSize: 16, //cssFluidSize([16, 18]),
      fontWeight: 700,
      lineHeight: 27 / 18,
      fontFamily: fontFamilyHeadings,
      textTransform: 'none',
    },
    // Disable h3 variant
    body2: undefined,
    // subtitle1: undefined,
    // subtitle2: undefined,
    caption: undefined,
    overline: undefined,
  },
})

export const applyTypography = (baseTheme: Theme) => {
  baseTheme.components = deepmerge(baseTheme.components, theme.components)
  baseTheme.typography = deepmerge(baseTheme.typography, theme.typography)
  return baseTheme;
}

export default theme